/**
 * Per-PR quick-change preview helpers.
 *
 * The quick-change service deploys per-PR previews under
 *   https://staging.console.workloads.netapp.com/previews/pr-<n>/
 * so designers / PMs / engineers can review a Copilot iteration
 * before merging the PR.
 *
 * The preview is the SAME shell + MFE bundles you get on live
 * staging — built from the PR branch — running on the SAME origin.
 * That last bit matters:
 *
 *   1. The Auth0 session cookie is shared with live staging, so a
 *      user already logged into staging in another tab gets a token
 *      silently via Auth.refreshSso (no redirect needed).
 *   2. All runtime fetches (services.json, per-MFE workload-config,
 *      MFE bundles) need to resolve under the preview prefix
 *      instead of the origin root, otherwise the preview shell ends
 *      up loading the LIVE staging MFE bundles and you don't see
 *      the change.
 *
 * Everything in this module is defensive: it returns the "not in
 * preview mode" answer whenever there's any doubt, so a typo in a
 * URL or a stray `/previews/` path elsewhere can't accidentally
 * trigger the preview code path.
 */

const PREVIEW_PATH_RE = /^\/previews\/pr-(\d+)\//;

/**
 * Returns true when the current page was loaded from a per-PR
 * preview path. Safe to call from non-browser environments (returns
 * false during SSR / tests without a window).
 */
export function isPreviewMode(pathname?: string): boolean {
    const path = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
    return PREVIEW_PATH_RE.test(path);
}

/**
 * Returns the preview prefix WITHOUT a trailing slash, suitable for
 * concatenating with absolute paths:
 *
 *   getPreviewPrefix() + '/services/services.json'
 *
 * Returns "" when not in preview mode.
 */
export function getPreviewPrefix(pathname?: string): string {
    const path = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
    const match = path.match(PREVIEW_PATH_RE);
    if (!match) return '';
    // match[0] is e.g. "/previews/pr-13394/"; strip the trailing slash
    // so callers can do `${prefix}/services/services.json` and get
    // exactly one slash between prefix and path.
    return match[0].slice(0, -1);
}

/**
 * PR number the user is previewing, or undefined when not in
 * preview mode. Used for telemetry / "preview not found" messages.
 */
export function getPreviewPrNumber(pathname?: string): number | undefined {
    const path = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
    const match = path.match(PREVIEW_PATH_RE);
    return match ? Number(match[1]) : undefined;
}
